import { useState, useEffect } from 'react';
import { Menu, ArrowUp, FileText, User, Calendar, Tag } from 'lucide-react';
import { Sidebar } from './components/Sidebar';
import { navSections } from './data/navigation';
import { AgentInstallationSection } from './sections/AgentInstallationSection';
import { CredentialsConfigurationSection } from './sections/CredentialsConfigurationSection';
import { ReferenceSection } from './sections/ReferenceSection';

export default function SOPPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('overview');
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [installationTab, setInstallationTab] = useState<'online' | 'offline'>('online');

    useEffect(() => {
        const mainContent = document.querySelector('main.overflow-y-auto');
        const scrollTarget = mainContent || window;

        const handleScroll = () => {
            const scrollY = mainContent ? (mainContent as HTMLElement).scrollTop : window.scrollY;
            setShowBackToTop(scrollY > 400);

            const sections = document.querySelectorAll('section.doc-section');
            let current = 'overview';

            sections.forEach((section) => {
                const sectionRect = section.getBoundingClientRect();
                const containerRect = mainContent ? (mainContent as HTMLElement).getBoundingClientRect() : { top: 0 };

                // Calculate position relative to container
                const sectionTop = sectionRect.top - containerRect.top + scrollY;

                if (scrollY >= sectionTop - 150) {
                    current = section.getAttribute('id') || 'overview';
                }
            });
            setActiveSection(current);
        };

        scrollTarget.addEventListener('scroll', handleScroll);
        return () => scrollTarget.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollTo = (id: string) => {
        // Special case for installation tabs
        let targetId = id;
        if (id === 'lw-online') {
            setInstallationTab('online');
        } else if (id === 'lw-offline') {
            setInstallationTab('offline');
            targetId = 'lw-online'; // The section has id="lw-online"
        }

        const element = document.getElementById(targetId);
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
            setActiveSection(id);
        }
    };

    const filteredNav = navSections.map(group => ({
        ...group,
        items: group.items.filter(item =>
            item.label.toLowerCase().includes(searchQuery.toLowerCase())
        )
    })).filter(group => group.items.length > 0);

    return (
        <div className="flex min-h-screen bg-slate-50 dark:bg-gray-900 text-slate-800 dark:text-gray-200 font-sans selection:bg-orange-500/30 selection:text-orange-700 dark:selection:text-orange-200">

            <Sidebar
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                activeSection={activeSection}
                scrollTo={scrollTo}
                filteredNav={filteredNav}
            />

            {/* Main Content */}
            <main className="flex-1 lg:ml-[260px] relative">
                {/* Mobile Header */}
                <header className="lg:hidden sticky top-0 z-[80] flex items-center justify-between px-4 h-14 bg-white/80 dark:bg-gray-950/80 backdrop-blur-md border-b border-slate-200 dark:border-white/5">
                    <div className="flex items-center gap-2.5">
                        <img src="/owllogopng.png" alt="Logo" className="w-7 h-7" />
                        <span className="font-bold text-[13px] text-slate-900 dark:text-gray-100 tracking-tight">Ovul Scanner</span>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="p-1.5 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                    >
                        <Menu size={22} />
                    </button>
                </header>

                {/* Hero */}
                <div className="relative p-4 overflow-hidden border-b border-slate-200 dark:border-white/5">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 rounded-full text-xs font-semibold text-orange-600 dark:text-orange-400 mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                        Standard Operating Procedure
                    </div>

                    <h1 className="text-xl lg:text-2xl font-extrabold tracking-tight leading-tight mb-2 text-gray-900 dark:text-white">
                        Ovul Scanner — Agent Installation & Configuration Guide
                    </h1>

                    <p className="text-[14px] text-slate-600 dark:text-gray-200 leading-relaxed mb-5">
                        Complete documentation for installing, configuring, and managing End Point and Master
                        Gateway Agents across Windows, Linux, macOS, and ARM platforms.
                    </p>

                    <div className="flex flex-wrap gap-4">
                        {[
                            { icon: <FileText size={16} />, label: 'Document ID:', val: 'TBD', color: 'text-amber-500 dark:text-yellow-400' },
                            { icon: <User size={16} />, label: 'Prepared By:', val: 'Runners', color: 'text-blue-600 dark:text-blue-400' },
                            { icon: <Calendar size={16} />, label: 'Last Updated:', val: 'May 2026', color: 'text-purple-600 dark:text-purple-400' },
                            { icon: <Tag size={16} />, label: 'Version:', val: '1.0', color: 'text-emerald-600 dark:text-emerald-400' }
                        ].map(meta => (
                            <div key={meta.label} className="flex items-center gap-1.5 text-[12px]">
                                <span className={meta.color}>{meta.icon}</span>
                                <span className="text-slate-600 dark:text-gray-200 font-medium">{meta.label}</span>
                                <span className="text-slate-900 dark:text-gray-300 font-semibold">{meta.val}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="p-4 lg:p-6 mb-16">
                    <AgentInstallationSection
                        activeMethod={installationTab}
                        setActiveMethod={setInstallationTab}
                    />
                    <CredentialsConfigurationSection />
                    <ReferenceSection />
                </div>
            </main>

            {/* Back to Top */}
            <button
                className={`fixed bottom-8 right-8 z-50 w-11 h-11 bg-orange-600 hover:bg-orange-500 text-white border-none rounded-full flex items-center justify-center text-xl shadow-[0_8px_32px_rgba(0,0,0,0.2),0_0_20px_rgba(249,115,22,0.3)] transition-all duration-300 hover:-translate-y-1 ${showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
                onClick={() => {
                    const mainContent = document.querySelector('main.overflow-y-auto');
                    if (mainContent) {
                        mainContent.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                }}
            >
                <ArrowUp size={20} />
            </button>
        </div>
    );
}
